/**
 * Button component following FP principles
 */
import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'gold';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

type ButtonProps = Readonly<{
  children: React.ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}>;

/**
 * Pure function to compute button classes
 */
const getButtonClasses = (
  variant: ButtonVariant,
  size: ButtonSize,
  fullWidth: boolean,
  disabled: boolean,
  className: string
): string => {
  const baseClasses = 'font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900';
  
  const variantClasses: Record<ButtonVariant, string> = {
    primary: 'bg-white text-gray-900 hover:bg-gray-100 focus:ring-white',
    secondary: 'bg-brand-gray-800 text-white hover:bg-brand-gray-700 focus:ring-brand-gray-600',
    outline: 'border-2 border-white text-white hover:bg-white hover:text-gray-900 focus:ring-white',
    ghost: 'text-white hover:bg-white/10 focus:ring-white/50',
    gold: 'bg-brand-gold text-gray-900 hover:bg-brand-gold-light focus:ring-brand-gold',
  };
  
  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl',
  };
  
  const widthClass = fullWidth ? 'w-full' : '';
  const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
  
  return [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    widthClass,
    disabledClass,
    className,
  ].filter(Boolean).join(' ');
};

/**
 * Button component
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  fullWidth = false,
  type = 'button',
  className = '',
}) => {
  const classes = getButtonClasses(variant, size, fullWidth, disabled, className);
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
    >
      {children}
    </button>
  );
};
